import { useCallback } from 'react'

import { Heading } from '@circlefin/components'
import { useForm, y } from '@circlefin/form'
import { Center } from '@shared/components/layout'
import { BeneficiaryType } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

const schema = y.object({
  /**
   * Beneficiary type.
   */
  type: y.string().oneOf(Object.values(BeneficiaryType)).required(),
  /**
   * Beneficiary first name.
   */
  firstName: y.string().when('type', {
    is: BeneficiaryType.individual,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
  /**
   * Beneficiary last name.
   */
  lastName: y.string().when('type', {
    is: BeneficiaryType.individual,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
  /**
   * Beneficiary business name.
   */
  businessName: y.string().when('type', {
    is: BeneficiaryType.business,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.notRequired(),
  }),
})

export type BeneficiaryInfoFormValues = y.InferType<typeof schema>

export interface BeneficiaryInfoFormProps {
  /**
   * On Form Submit callback.
   */
  onSubmit: (values: BeneficiaryInfoFormValues) => void
  /**
   * Loading state of mutation.
   */
  loading: boolean
}

export const BeneficiaryInfoForm: React.FC<BeneficiaryInfoFormProps> = ({
  onSubmit,
  loading,
}) => {
  const { t } = useTranslation('address-book/modals')

  const [Form, { watch, resetField }] = useForm<BeneficiaryInfoFormValues>({
    schema,
  })

  const beneficiaryType = watch('type')

  // Reset fields when type changes
  const handleTypeSelected = useCallback(
    (type: BeneficiaryType) => () => {
      if (type === BeneficiaryType.individual) {
        resetField('businessName')
      }

      if (type === BeneficiaryType.business) {
        resetField('firstName')
        resetField('lastName')
      }
    },
    [resetField],
  )

  return (
    <Center variant="horizontal">
      <Form onSubmit={onSubmit}>
        <div className="flex w-128 flex-col gap-y-6">
          <Form.SelectCard
            label="Business"
            name="type"
            onChange={handleTypeSelected(BeneficiaryType.business)}
            type="radio"
            value="business"
          >
            <Heading iconName="OfficeBuildingSolid">
              <Heading.Header>
                {t('addAddress.beneficiaryInfo.business')}
              </Heading.Header>
            </Heading>
          </Form.SelectCard>
          <Form.SelectCard
            label="Individual"
            name="type"
            onChange={handleTypeSelected(BeneficiaryType.individual)}
            type="radio"
            value="individual"
          >
            <Heading iconName="UserSolid">
              <Heading.Header>
                {t('addAddress.beneficiaryInfo.individual')}
              </Heading.Header>
            </Heading>
          </Form.SelectCard>
        </div>

        {/* Hide second half of the form until type is selected */}
        {Object.values(BeneficiaryType).includes(beneficiaryType) && (
          <div className="flex flex-col gap-y-8 pt-16">
            <div className="flex flex-col items-center pb-6">
              <h2 className="type-h-section-md">
                {t(
                  `addAddress.beneficiaryInfo.${
                    beneficiaryType === BeneficiaryType.individual
                      ? 'addIndividual'
                      : 'addBusiness'
                  }`,
                )}
              </h2>
              <h3 className="text-neutral-subtle type-body-base-bold">
                {t('addAddress.beneficiaryInfo.provideDetails')}
              </h3>
            </div>
            <div className="flex flex-col gap-y-4">
              {beneficiaryType === BeneficiaryType.individual ? (
                <>
                  <Form.Input
                    className="w-full"
                    label={t('addAddress.beneficiaryInfo.firstName')}
                    name="firstName"
                  />
                  <Form.Input
                    className="w-full"
                    label={t('addAddress.beneficiaryInfo.lastName')}
                    name="lastName"
                  />
                </>
              ) : (
                <Form.Input
                  className="w-full"
                  label={t('addAddress.beneficiaryInfo.businessName')}
                  name="businessName"
                />
              )}
              <Form.SubmitButton
                className="w-full"
                loading={loading}
                variant="primary"
              >
                {t`common:submit`}
              </Form.SubmitButton>
            </div>
          </div>
        )}
      </Form>
    </Center>
  )
}
